import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { quotationsAPI, clientsAPI, itemsAPI, settingsAPI } from '../utils/api';
import { format, addDays } from 'date-fns';
import { 
  FiPlus, FiTrash2, FiSave, FiX, FiSearch
} from 'react-icons/fi';
import './CreateQuotation.css';

const CreateQuotation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [displayFields, setDisplayFields] = useState({
    sku: true,
    category: true,
    item_name: true,
    description: true,
    unit_price: true,
    unit: true,
    quantity: true,
    tax: true,
  });
  
  const [customItem, setCustomItem] = useState({
    name: '',
    sku: '',
    category: '',
    description: '',
    quantity: 1,
    unitPrice: '',
    unit: 'pcs',
    taxRate: 0,
  });
  
  // Payment Terms Preset Templates
  const paymentTemplates = [
    { id: 'full', name: 'Full Payment', installments: [{ percentage: 100, due_days: 0, description: 'Full payment' }] },
    { id: '50-50', name: '50% / 50%', installments: [
      { percentage: 50, due_days: 0, description: 'First payment' },
      { percentage: 50, due_days: 30, description: 'Final payment' }
    ]},
    { id: '30-40-30', name: '30% / 40% / 30%', installments: [
      { percentage: 30, due_days: 0, description: 'Deposit' },
      { percentage: 40, due_days: 30, description: 'Progress payment' },
      { percentage: 30, due_days: 60, description: 'Final payment' }
    ]},
    { id: 'net30', name: 'Net 30 Days', installments: [{ percentage: 100, due_days: 30, description: 'Full payment' }] },
    { id: 'net60', name: 'Net 60 Days', installments: [{ percentage: 100, due_days: 60, description: 'Full payment' }] },
    { id: 'deposit-50', name: '50% Deposit + Balance', installments: [
      { percentage: 50, due_days: 0, description: 'Deposit' },
      { percentage: 50, due_days: 14, description: 'Balance upon completion' }
    ]},
  ];

  const [formData, setFormData] = useState({
    clientId: searchParams.get('clientId') || '',
    client: null,
    issueDate: format(new Date(), 'yyyy-MM-dd'),
    expiryDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    currency: 'AED',
    poNumber: '',
    notes: '',
    terms: 'This quotation is valid for 30 days from the issue date.',
    discountType: 'percentage',
    discountValue: 0,
    items: [],
    paymentTermsType: 'full',
    dueDateType: 'days', // 'days' or 'specific'
    deposit: { enabled: false, percentage: 0, description: 'Deposit' },
    installments: [
      { percentage: 50, due_days: 0, due_date: null, description: 'First payment', milestone: '' }
    ],
  });

  useEffect(() => {
    if (formData.clientId) {
      fetchClient(formData.clientId);
    }
    fetchItems();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getAll();
      const settings = response.data.settings;
      if (settings.tax_enabled) {
        setTaxEnabled(settings.tax_enabled.value === 'true');
      }
      // Load display fields settings
      if (settings.display_fields) {
        try {
          const parsed = typeof settings.display_fields.value === 'string' 
            ? JSON.parse(settings.display_fields.value) 
            : settings.display_fields.value;
          setDisplayFields(prev => ({ ...prev, ...parsed }));
        } catch (e) {
          console.error('Failed to parse display_fields:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const fetchClient = async (id) => {
    try {
      const response = await clientsAPI.getById(id);
      setFormData(prev => ({
        ...prev,
        client: response.data.client,
        clientId: id,
        currency: response.data.client.currency || 'AED',
      }));
    } catch (error) {
      toast.error('Failed to load client');
    }
  };

  const fetchItems = async () => {
    try {
      const response = await itemsAPI.getAll({ limit: 100 });
      setItems(response.data.items);
    } catch (error) {
      console.error('Failed to load items');
    }
  };

  const searchClients = async () => {
    try {
      const response = await clientsAPI.getAll({ search: clientSearch, limit: 10 });
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to search clients');
    }
  };

  const openClientSearch = async () => {
    setShowClientSearch(true);
    try {
      const response = await clientsAPI.getAll({ limit: 10 });
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const selectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      client,
      clientId: client.id,
      currency: client.currency || 'AED',
    }));
    setShowClientSearch(false);
    setClientSearch('');
  };

  const addItem = (item) => {
    const newItem = {
      itemId: item.id,
      name: item.name,
      sku: item.sku || '',
      category: item.category || '',
      description: item.description || '',
      quantity: 1,
      unitPrice: item.unit_price,
      unit: item.unit || 'pcs',
      taxRate: taxEnabled ? (item.tax_rate || 0) : 0,
      discountType: 'percentage',
      discountValue: 0,
      isCustom: false,
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
    setShowItemSearch(false);
    setItemSearch('');
  };

  const addCustomItem = () => {
    if (!customItem.name || !customItem.unitPrice) {
      toast.error('Item name and unit price are required');
      return;
    }

    const newItem = {
      itemId: null,
      name: customItem.name,
      sku: customItem.sku || '',
      category: customItem.category || '',
      description: customItem.description || '',
      quantity: parseFloat(customItem.quantity) || 1,
      unitPrice: parseFloat(customItem.unitPrice),
      unit: customItem.unit || 'pcs',
      taxRate: taxEnabled ? (parseFloat(customItem.taxRate) || 0) : 0,
      discountType: 'percentage',
      discountValue: 0,
      isCustom: true,
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
    
    setCustomItem({
      name: '',
      sku: '',
      category: '',
      description: '',
      quantity: 1,
      unitPrice: '',
      unit: 'pcs',
      taxRate: 0,
    });
    setShowCustomItemModal(false);
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unitPrice;
    const discount = item.discountType === 'percentage' 
      ? subtotal * (item.discountValue / 100)
      : item.discountValue;
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * (item.taxRate / 100);
    return {
      subtotal,
      discount,
      tax,
      total: afterDiscount + tax,
    };
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    formData.items.forEach(item => {
      const calc = calculateItemTotal(item);
      subtotal += calc.subtotal;
      totalTax += calc.tax;
      totalDiscount += calc.discount;
    });

    const quotationDiscount = formData.discountValue > 0
      ? (formData.discountType === 'percentage'
        ? (subtotal - totalDiscount) * (formData.discountValue / 100)
        : formData.discountValue)
      : 0;

    const total = subtotal - totalDiscount - quotationDiscount + totalTax;

    return {
      subtotal,
      tax: totalTax,
      discount: totalDiscount + quotationDiscount,
      total,
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formData.currency,
    }).format(amount || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.clientId) {
      toast.error('Please select a client');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setLoading(true);

    try {
      const data = {
        clientId: parseInt(formData.clientId),
        issueDate: formData.issueDate,
        expiryDate: formData.expiryDate,
        currency: formData.currency,
        poNumber: formData.poNumber,
        notes: formData.notes,
        terms: formData.terms,
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue) || 0,
        paymentTerms: formData.paymentTermsType === 'installments' ? {
          type: 'installments',
          installments: formData.installments
        } : { type: 'full' },
        items: formData.items.map(item => ({
          itemId: item.itemId,
          name: item.name,
          sku: item.sku || '',
          category: item.category || '',
          description: item.description,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          unit: item.unit,
          taxRate: parseFloat(item.taxRate) || 0,
          discountType: item.discountType,
          discountValue: parseFloat(item.discountValue) || 0,
        })),
      };

      const response = await quotationsAPI.create(data);
      toast.success('Quotation created successfully');
      navigate(`/quotations/${response.data.quotation.id}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create quotation');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="page-content create-quotation-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Create Quotation</h1>
          <p>Create a new quotation for your client</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/quotations')}>
            <FiX /> Cancel
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="quotation-form-grid">
          <div className="quotation-details">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Client</h3>
              </div>
              <div className="card-body">
                {formData.client ? (
                  <div className="selected-client">
                    <div className="client-info">
                      <h4>{formData.client.company_name}</h4>
                      <p>{formData.client.contact_person}</p>
                      <p className="text-muted">{formData.client.email}</p>
                    </div>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={openClientSearch}>
                      Change
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn btn-secondary btn-block" onClick={openClientSearch}>
                    <FiSearch /> Select Client
                  </button>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Quotation Details</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Issue Date</label>
                    <input
                      type="date"
                      value={formData.issueDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, issueDate: e.target.value }))}
                      className="form-control"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Expiry Date</label>
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, expiryDate: e.target.value }))}
                      className="form-control"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                      className="form-control"
                    >
                      <option value="AED">AED - UAE Dirham</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">PO Number</label>
                    <input
                      type="text"
                      value={formData.poNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, poNumber: e.target.value }))}
                      className="form-control"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="form-control"
                    rows={3}
                    placeholder="Notes visible to client"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Terms & Conditions</label>
                  <textarea
                    value={formData.terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                    className="form-control"
                    rows={3}
                  />
                </div>

                {/* Payment Terms Section */}
                <div className="payment-terms-section">
                  <div className="payment-terms-header">
                    <span className="payment-terms-title">Payment Terms</span>
                    <span className="payment-terms-hint">How the total is collected from the client</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Terms</label>
                    <select
                      value={formData.paymentTermsType}
                      onChange={(e) => {
                        const type = e.target.value;
                        if (type === 'full') {
                          setFormData(prev => ({
                            ...prev,
                            paymentTermsType: type,
                            installments: [{ percentage: 100, due_days: 0, due_date: null, description: 'Full payment', milestone: '' }]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            paymentTermsType: type,
                            installments: [
                              { percentage: 50, due_days: 0, due_date: null, description: 'First payment', milestone: '' },
                              { percentage: 50, due_days: 30, due_date: null, description: 'Final payment', milestone: '' }
                            ]
                          }));
                        }
                      }}
                      className="form-control"
                    >
                      <option value="full">Full Payment</option>
                      <option value="installments">Installments</option>
                    </select>
                  </div>

                  {formData.paymentTermsType === 'full' && (
                    <p className="payment-terms-summary">
                      Full amount due once the quotation is accepted: <strong>{formatCurrency(totals.total)}</strong>
                    </p>
                  )}

                  {/* Preset Templates */}
                  {formData.paymentTermsType === 'installments' && (
                    <div className="form-group">
                      <label className="form-label">Quick Templates</label>
                      <div className="template-buttons">
                        {paymentTemplates.map(template => (
                          <button
                            key={template.id}
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              installments: template.installments.map(inst => ({
                                ...inst,
                                due_date: null,
                                milestone: ''
                              }))
                            }))}
                          >
                            {template.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Deposit Option */}
                  {formData.paymentTermsType === 'installments' && (
                    <div className="form-group deposit-option">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.deposit?.enabled || false}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            deposit: { ...prev.deposit, enabled: e.target.checked }
                          }))}
                        />
                        <span>Require deposit before work begins</span>
                      </label>
                      {formData.deposit?.enabled && (
                        <div className="deposit-fields">
                          <div className="form-row">
                            <div className="form-group">
                              <label className="form-label">Deposit %</label>
                              <input
                                type="number"
                                value={formData.deposit?.percentage || 0}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  deposit: { ...prev.deposit, percentage: parseFloat(e.target.value) || 0 }
                                }))}
                                className="form-control"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div className="form-group deposit-amount">
                              <label className="form-label">Amount</label>
                              <div className="deposit-amount-value">
                                {formatCurrency(totals.total * ((formData.deposit?.percentage || 0) / 100))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Due Date Type Selection */}
                  {formData.paymentTermsType === 'installments' && (
                    <div className="form-group">
                      <label className="form-label">Due Date Type</label>
                      <div className="due-date-type-toggle">
                        <button
                          type="button"
                          className={`btn btn-sm ${formData.dueDateType === 'days' ? 'btn-primary' : 'btn-outline'}`}
                          onClick={() => setFormData(prev => ({ ...prev, dueDateType: 'days' }))}
                        >
                          Days after acceptance
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${formData.dueDateType === 'specific' ? 'btn-primary' : 'btn-outline'}`}
                          onClick={() => setFormData(prev => ({ ...prev, dueDateType: 'specific' }))}
                        >
                          Specific dates
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Installments Schedule */}
                  {formData.paymentTermsType === 'installments' && (
                    <div className="installments-section">
                      <div className="installments-header">
                        <label className="form-label">Installments Schedule</label>
                        <div className="installments-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => {
                              const count = formData.installments.length;
                              const evenPercentage = Math.round(100 / count);
                              const remainder = 100 - (evenPercentage * count);
                              setFormData(prev => ({
                                ...prev,
                                installments: prev.installments.map((inst, i) => ({
                                  ...inst,
                                  percentage: i === 0 ? evenPercentage + remainder : evenPercentage
                                }))
                              }));
                            }}
                            title="Distribute evenly"
                          >
                            ↻ Auto-distribute
                          </button>
                        </div>
                      </div>

                      {/* Visual Progress Bar */}
                      <div className="payment-progress">
                        <div className="progress-bar">
                          {formData.installments.map((inst, index) => (
                            <div
                              key={index}
                              className="progress-segment"
                              style={{
                                width: `${inst.percentage}%`,
                                backgroundColor: index % 2 === 0 ? '#8b5cf6' : '#6366f1'
                              }}
                              title={`Payment ${index + 1}: ${inst.percentage}%`}
                            />
                          ))}
                        </div>
                        <div className="progress-labels">
                          {formData.installments.map((inst, index) => (
                            <div
                              key={index}
                              className="progress-label"
                              style={{ width: `${inst.percentage}%` }}
                            >
                              {inst.percentage}%
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Installment Cards */}
                      <div className="installment-cards">
                        {formData.installments.map((inst, index) => (
                          <div key={index} className="installment-card">
                            <div className="installment-card-header">
                              <h4>Payment {index + 1}</h4>
                              {formData.installments.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger btn-icon"
                                  onClick={() => {
                                    const newInstallments = formData.installments.filter((_, i) => i !== index);
                                    setFormData(prev => ({ ...prev, installments: newInstallments }));
                                  }}
                                >
                                  <FiTrash2 />
                                </button>
                              )}
                            </div>
                            
                            <div className="installment-card-body">
                              <div className="form-row">
                                <div className="form-group">
                                  <label className="form-label">Percentage (%)</label>
                                  <input
                                    type="number"
                                    value={inst.percentage}
                                    onChange={(e) => {
                                      const newInstallments = [...formData.installments];
                                      newInstallments[index] = { ...newInstallments[index], percentage: parseFloat(e.target.value) || 0 };
                                      setFormData(prev => ({ ...prev, installments: newInstallments }));
                                    }}
                                    className="form-control"
                                    min="0"
                                    max="100"
                                    step="1"
                                  />
                                </div>
                                <div className="form-group installment-amount-display">
                                  <label className="form-label">Amount</label>
                                  <div className="installment-amount-value">
                                    {formatCurrency(totals.total * (inst.percentage / 100))}
                                  </div>
                                </div>
                              </div>

                              {formData.dueDateType === 'days' ? (
                                <div className="form-group">
                                  <label className="form-label">Due (days after acceptance)</label>
                                  <input
                                    type="number"
                                    value={inst.due_days || 0}
                                    onChange={(e) => {
                                      const newInstallments = [...formData.installments];
                                      newInstallments[index] = { ...newInstallments[index], due_days: parseInt(e.target.value) || 0 };
                                      setFormData(prev => ({ ...prev, installments: newInstallments }));
                                    }}
                                    className="form-control"
                                    min="0"
                                    placeholder="0 = upon acceptance"
                                  />
                                </div>
                              ) : (
                                <div className="form-group">
                                  <label className="form-label">Due Date</label>
                                  <input
                                    type="date"
                                    value={inst.due_date || ''}
                                    onChange={(e) => {
                                      const newInstallments = [...formData.installments];
                                      newInstallments[index] = { ...newInstallments[index], due_date: e.target.value };
                                      setFormData(prev => ({ ...prev, installments: newInstallments }));
                                    }}
                                    className="form-control"
                                    min={formData.issueDate}
                                  />
                                </div>
                              )}

                              {/* Milestone Selection */}
                              <div className="form-group">
                                <label className="form-label">Milestone (optional)</label>
                                <select
                                  value={inst.milestone || ''}
                                  onChange={(e) => {
                                    const newInstallments = [...formData.installments];
                                    newInstallments[index] = { ...newInstallments[index], milestone: e.target.value };
                                    setFormData(prev => ({ ...prev, installments: newInstallments }));
                                  }}
                                  className="form-control"
                                >
                                  <option value="">Select milestone...</option>
                                  <option value="upon_acceptance">Upon acceptance</option>
                                  <option value="upon_approval">Upon approval</option>
                                  <option value="upon_delivery">Upon delivery</option>
                                  <option value="upon_completion">Upon completion</option>
                                  <option value="upon_installation">Upon installation</option>
                                  <option value="upon_testing">Upon testing</option>
                                  <option value="project_start">Project start</option>
                                  <option value="project_midpoint">Project midpoint</option>
                                  <option value="project_end">Project end</option>
                                </select>
                              </div>

                              {/* Description/Notes */}
                              <div className="form-group">
                                <label className="form-label">Description/Notes</label>
                                <input
                                  type="text"
                                  value={inst.description || ''}
                                  onChange={(e) => {
                                    const newInstallments = [...formData.installments];
                                    newInstallments[index] = { ...newInstallments[index], description: e.target.value };
                                    setFormData(prev => ({ ...prev, installments: newInstallments }));
                                  }}
                                  className="form-control"
                                  placeholder="e.g., Initial deposit, Final payment"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-secondary add-installment-btn"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            installments: [...prev.installments, { percentage: 0, due_days: 0, due_date: null, description: '', milestone: '' }]
                          }));
                        }}
                      >
                        <FiPlus /> Add Installment
                      </button>

                      {/* Validation Warning */}
                      {formData.installments.reduce((sum, i) => sum + i.percentage, 0) !== 100 && (
                        <p className="installment-warning">
                          ⚠️ Total percentage must equal 100% (currently {formData.installments.reduce((sum, i) => sum + i.percentage, 0)}%)
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="quotation-items">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Items</h3>
                <div className="card-header-actions">
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowItemSearch(true)}>
                    <FiPlus /> From Catalog
                  </button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowCustomItemModal(true)}>
                    <FiPlus /> Custom
                  </button>
                </div>
              </div>
              <div className="card-body">
                {formData.items.length === 0 ? (
                  <div className="empty-state">
                    <p>No items added yet</p>
                    <div className="empty-state-actions">
                      <button type="button" className="btn btn-primary" onClick={() => setShowItemSearch(true)}>
                        <FiPlus /> From Catalog
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => setShowCustomItemModal(true)}>
                        <FiPlus /> Custom Item
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="items-list">
                    {formData.items.map((item, index) => {
                      const calc = calculateItemTotal(item);
                      return (
                        <div key={index} className={`quotation-item ${item.isCustom ? 'custom-item' : ''}`}>
                          <div className="item-header">
                            <div className="item-name-input">
                              <span className="item-index">{index + 1}</span>
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) => updateItem(index, 'name', e.target.value)}
                                className="form-control item-name-field"
                                placeholder="Item name"
                              />
                              {item.isCustom && <span className="custom-badge">Custom</span>}
                            </div>
                            <button type="button" className="btn btn-sm btn-danger item-remove-btn" onClick={() => removeItem(index)}>
                              <FiTrash2 />
                            </button>
                          </div>
                          <div className="item-fields" style={{ padding: '16px', display: 'block' }}>
                            {(displayFields.sku || displayFields.category) && (
                              <div className="form-row" style={{ marginBottom: '12px' }}>
                                {displayFields.sku && (
                                  <div className="form-group">
                                    <label className="form-label">SKU</label>
                                    <input
                                      type="text"
                                      value={item.sku || ''}
                                      onChange={(e) => updateItem(index, 'sku', e.target.value)}
                                      className="form-control"
                                      placeholder="SKU"
                                    />
                                  </div>
                                )}
                                {displayFields.category && (
                                  <div className="form-group">
                                    <label className="form-label">Category</label>
                                    <input
                                      type="text"
                                      value={item.category || ''}
                                      onChange={(e) => updateItem(index, 'category', e.target.value)}
                                      className="form-control"
                                      placeholder="Category"
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                            {displayFields.description && (
                            <div className="form-group description-group" style={{ marginBottom: '12px' }}>
                              <label className="form-label">Description</label>
                              <textarea
                                value={item.description}
                                onChange={(e) => updateItem(index, 'description', e.target.value)}
                                className="form-control"
                                rows={2}
                                placeholder="Item description..."
                              />
                            </div>
                            )}
                            <div className="item-pricing-row" style={{ display: 'grid', gridTemplateColumns: `72px 1fr ${taxEnabled ? '72px' : ''} 150px`, gap: '12px', alignItems: 'end' }}>
                              {(
                                <div className="form-group">
                                  <label className="form-label">Qty</label>
                                  <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                    className="form-control"
                                    min="0.01"
                                    step="0.01"
                                  />
                                </div>
                              )}
                              {(
                                <div className="form-group">
                                  <label className="form-label">Unit Price</label>
                                  <input
                                    type="number"
                                    value={item.unitPrice}
                                    onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                                    className="form-control"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                              )}
                              {taxEnabled && (
                                <div className="form-group">
                                  <label className="form-label">Tax %</label>
                                  <input
                                    type="number"
                                    value={item.taxRate}
                                    onChange={(e) => updateItem(index, 'taxRate', e.target.value)}
                                    className="form-control"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                              )}
                              <div className="item-total-col">
                                <label className="form-label">Amount</label>
                                {/* Pre-VAT line amount, matching the final document */}
                                <div className="item-total-value">{formatCurrency(calc.total - calc.tax)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {formData.items.length > 0 && (
                  <div className="quotation-totals">
                    <div className="total-row">
                      <span>Subtotal</span>
                      <span>{formatCurrency(totals.subtotal)}</span>
                    </div>
                    <div className="total-row discount-input-row">
                      <span>Discount</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="number"
                          value={formData.discountValue}
                          onChange={(e) => setFormData(prev => ({ ...prev, discountValue: e.target.value }))}
                          className="form-control"
                          step="0.01"
                          min="0"
                          style={{ width: '100px' }}
                          placeholder="0"
                        />
                        <select
                          value={formData.discountType}
                          onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                          className="form-control"
                          style={{ width: '85px' }}
                        >
                          <option value="percentage">%</option>
                          <option value="fixed">AED</option>
                        </select>
                      </div>
                    </div>
                    {totals.discount > 0 && (
                      <div className="total-row discount">
                        <span>Discount Amount</span>
                        <span>-{formatCurrency(totals.discount)}</span>
                      </div>
                    )}
                    {taxEnabled && (
                      <div className="total-row">
                        <span>Tax</span>
                        <span>{formatCurrency(totals.tax)}</span>
                      </div>
                    )}
                    <div className="total-row grand-total">
                      <span>Total</span>
                      <span>{formatCurrency(totals.total)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg btn-block"
                  disabled={loading || formData.items.length === 0}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FiSave /> Create Quotation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Client Search Modal */}
      {showClientSearch && (
        <div className="modal-overlay" onClick={() => setShowClientSearch(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Select Client</h2>
              <button className="modal-close" onClick={() => setShowClientSearch(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="search-bar">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search clients..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                />
                <button type="button" className="btn btn-primary" onClick={searchClients}>
                  Search
                </button>
              </div>
              <div className="client-list">
                {clients.map(client => (
                  <div key={client.id} className="client-option" onClick={() => selectClient(client)}>
                    <div className="client-option-name">{client.company_name}</div>
                    <div className="client-option-email">{client.email}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Search Modal */}
      {showItemSearch && (
        <div className="modal-overlay" onClick={() => setShowItemSearch(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Select Item from Catalog</h2>
              <button className="modal-close" onClick={() => setShowItemSearch(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="search-bar">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search items..."
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                />
              </div>
              <div className="item-list">
                {items
                  .filter(item => 
                    item.is_active && 
                    (item.name.toLowerCase().includes(itemSearch.toLowerCase()) ||
                     (item.sku && item.sku.toLowerCase().includes(itemSearch.toLowerCase()))))
                  .map(item => (
                    <div key={item.id} className="item-option" onClick={() => addItem(item)}>
                      <div className="item-option-name">{item.name}</div>
                      <div className="item-option-price">{formatCurrency(item.unit_price)} / {item.unit}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Item Modal */}
      {showCustomItemModal && (
        <div className="modal-overlay" onClick={() => setShowCustomItemModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add Custom Item</h2>
              <button className="modal-close" onClick={() => setShowCustomItemModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label required">Item Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={customItem.name}
                  onChange={(e) => setCustomItem(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter item name"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">SKU</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customItem.sku}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, sku: e.target.value }))}
                    placeholder="Enter SKU (optional)"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customItem.category}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="Enter category (optional)"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  value={customItem.description}
                  onChange={(e) => setCustomItem(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description (optional)"
                  rows={2}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">Quantity</label>
                  <input
                    type="number"
                    className="form-control"
                    value={customItem.quantity}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, quantity: e.target.value }))}
                    min="0.01"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Unit</label>
                  <select
                    className="form-control"
                    value={customItem.unit}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, unit: e.target.value }))}
                  >
                    <option value="pcs">Pieces (pcs)</option>
                    <option value="hrs">Hours (hrs)</option>
                    <option value="days">Days</option>
                    <option value="units">Units</option>
                    <option value="m">Meters (m)</option>
                    <option value="kg">Kilograms (kg)</option>
                    <option value="l">Liters (l)</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">Unit Price</label>
                  <input
                    type="number"
                    className="form-control"
                    value={customItem.unitPrice}
                    onChange={(e) => setCustomItem(prev => ({ ...prev, unitPrice: e.target.value }))}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
                {taxEnabled && (
                  <div className="form-group">
                    <label className="form-label">Tax Rate (%)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={customItem.taxRate}
                      onChange={(e) => setCustomItem(prev => ({ ...prev, taxRate: e.target.value }))}
                      step="0.01"
                      min="0"
                      placeholder="0"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCustomItemModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={addCustomItem}>
                <FiPlus /> Add Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateQuotation;